import { MobileTutorial } from './components/MobileTutorial'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { BrowserRouter, MemoryRouter } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { assistantsApi } from '@shared/api/assistants'
import { getOrganization } from '@shared/api/organizationApi'
import { useAuthStore } from '@shared/store/authStore'
import { RedesignApp } from './RedesignApp'
import { ErrorState } from './components/States'
import { RedesignProvider } from './context'
import { useRefreshOnResume } from './data/hooks'
import { createDeviceHubQueue } from './data/hubQueue/device'
import { createLiveSliceApi } from './data/liveApi'
import {
  OFFLINE_MAX_AGE_MS,
  persistOfflineCache,
  restoreOfflineCache,
  type CacheOwner,
  type OfflineStore,
} from './data/offlineCache'
import { setRedesignEnabled } from './flag'
import { reportLegacySwitch } from './legacyUsage'
import type { Platform } from './platform/platform'
import { resolveScheme, useThemeChoice } from './platform/theme'
import { SetupScreen } from './screens/SetupScreen'

function Loading() {
  return (
    <div className="pc2" style={{ display: 'grid', placeItems: 'center' }} role="status">
      Загрузка…
    </div>
  )
}

/**
 * Новый интерфейс на боевых данных. Клуб и направление берутся из того же
 * стора, что у старого интерфейса, вход тоже общий.
 *
 * router: в PWA - адресная строка браузера (работает системный «назад»), в
 * мини-приложениях - память: адрес Mini App занят параметрами запуска, а
 * «назад» там системной кнопкой мессенджера.
 *
 * preferencesLoading: пока настройки пользователя не пришли, направления в
 * сторе ещё нет; решать «нужен ли мастер» до них нельзя - перетрём выбор.
 *
 * offlineStore: только PWA (этап 4) - кэш экранов сохраняется на устройстве и
 * поднимается до первого экрана, чтобы без сети было что показать. В
 * мини-приложениях не передаётся: они живут внутри мессенджера.
 *
 * onSignOut: только PWA - выход из аккаунта (стереть вход и данные на
 * устройстве, открыть экран входа). В мини-приложениях вход через
 * мессенджер, выхода нет, и кнопки «Выйти» там не показываем.
 */
export function LiveRedesign({
  platform,
  router,
  preferencesLoading = false,
  offlineStore,
  onSignOut,
}: {
  platform: Platform
  router: 'browser' | 'memory'
  preferencesLoading?: boolean
  offlineStore?: OfflineStore
  onSignOut?: () => void | Promise<void>
}) {
  const assistantId = useAuthStore((s) => s.currentAssistantId)
  const organizationId = useAuthStore((s) => s.selectedOrganizationId)
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const client = useQueryClient()
  const [setup, setSetup] = useState(false)
  const scheme = resolveScheme(useThemeChoice(), platform)

  const scope = useRef({ assistantId, organizationId, userId })
  scope.current = { assistantId, organizationId, userId }
  const api = useMemo(() => createLiveSliceApi(() => scope.current), [])
  // Очередь Штаба на устройстве - только там, где Штаб - чат (PWA). Владелец -
  // вошедший пользователь: чужой на этом устройстве её не видит и не отправляет.
  const hubQueue = useMemo(
    () =>
      platform.hubMode === 'chat'
        ? createDeviceHubQueue({
            api,
            owner: () => scope.current.userId,
            assistant: () => scope.current.assistantId,
          })
        : undefined,
    [api, platform.hubMode],
  )

  // Вошёл другой пользователь - уходит его очередь
  useEffect(() => {
    if (userId) void hubQueue?.kick()
  }, [hubQueue, userId])

  // Вернулись в мини-приложение из бота (SC12) - экраны перечитывают данные
  // фоном. Одна подписка на всё приложение; в PWA ничего не делает.
  useRefreshOnResume(platform)

  // Сменили клуб или направление - открытые экраны перечитывают данные. При
  // первом показе не сбрасываем: там может лежать поднятый с устройства кэш.
  const shownScope = useRef({ assistantId, organizationId })
  useEffect(() => {
    const prev = shownScope.current
    shownScope.current = { assistantId, organizationId }
    if (prev.assistantId === assistantId && prev.organizationId === organizationId) return
    void client.resetQueries({ queryKey: ['redesign'] })
  }, [assistantId, organizationId, client])

  // Кэш на устройстве (PWA): поднять до первого экрана, потом сохранять.
  const owner: CacheOwner | null = useMemo(
    () => (userId && organizationId && assistantId ? { userId, organizationId, assistantId } : null),
    [userId, organizationId, assistantId],
  )
  const ownerRef = useRef(owner)
  ownerRef.current = owner
  const [restored, setRestored] = useState(!offlineStore)
  useEffect(() => {
    if (!offlineStore || !owner || restored) return
    let cancelled = false
    // Сохранённое держится в памяти весь срок хранения, а не 5 минут по
    // умолчанию: без сети тренер ходит по экранам, которые не открывал.
    client.setQueryDefaults(['redesign'], { gcTime: OFFLINE_MAX_AGE_MS })
    restoreOfflineCache(client, offlineStore, owner)
      .catch(() => false)
      .then(() => {
        if (!cancelled) setRestored(true)
      })
    return () => {
      cancelled = true
    }
  }, [offlineStore, owner, restored, client])
  useEffect(() => {
    if (!offlineStore || !restored) return
    return persistOfflineCache(client, offlineStore, () => {
      const current = ownerRef.current
      return current ? { owner: current, user: useAuthStore.getState().user } : null
    })
  }, [offlineStore, restored, client])

  // Направления нет в настройках: если в клубе направления есть - берём
  // первое (как прежний интерфейс), если нет - нужен мастер первого запуска.
  const needsCheck = !preferencesLoading && !assistantId && !!organizationId && !setup
  const directions = useQuery({
    queryKey: ['redesign-boot', 'directions', organizationId],
    queryFn: () => assistantsApi.getByOrganizationId(organizationId!),
    enabled: needsCheck,
  })
  const org = useQuery({
    queryKey: ['redesign-boot', 'org', organizationId],
    queryFn: () => getOrganization(organizationId!),
    enabled: setup && !!organizationId,
  })

  useEffect(() => {
    if (!needsCheck || !directions.data) return
    if (directions.data.length > 0) void useAuthStore.getState().syncCurrentAssistant(directions.data[0].id)
    else setSetup(true)
  }, [needsCheck, directions.data])

  const wrap = (children: ReactNode) =>
    router === 'browser' ? (
      <BrowserRouter>{children}</BrowserRouter>
    ) : (
      <MemoryRouter initialEntries={['/today']}>{children}</MemoryRouter>
    )

  if (setup) {
    return wrap(
      <RedesignProvider api={api} platform={platform} hubQueue={hubQueue} savesOffline={!!offlineStore}>
        <div className="pc2" data-theme={scheme}>
          <MobileTutorial setup /><SetupScreen key={organizationId} initialClubName={org.data?.name ?? ''} onDone={() => setSetup(false)} />
        </div>
      </RedesignProvider>,
    )
  }

  // Направления не загрузились (сервер или база лежат) - не вечная «Загрузка»,
  // а честная ошибка с повтором.
  if (!assistantId && directions.isError) {
    return (
      <div className="pc2" data-theme={scheme} style={{ padding: 16 }}>
        <ErrorState title="Не удалось загрузить направления клуба" onRetry={() => void directions.refetch()} />
      </div>
    )
  }

  if (!assistantId || !restored) return <Loading />

  return wrap(
    <RedesignProvider api={api} platform={platform} hubQueue={hubQueue} savesOffline={!!offlineStore}>
      <MobileTutorial />
      <RedesignApp
        onLegacy={(where) => void reportLegacySwitch(platform.kind, where).then(() => setRedesignEnabled(false))}
        onSignOut={onSignOut}
      />
    </RedesignProvider>,
  )
}
